/* eslint-disable camelcase */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from 'react-query';
import { useWalletAPIClient } from '@ledgerhq/wallet-api-client-react';
import { isMobile } from 'react-device-detect';
import BigNumber from 'bignumber.js';
import { SEND_MODES, SendCryptoScreensFlow } from '../../constants';
import { formatNumber } from '../../../../utilities/formatHelpers';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { USER_DETAILS_QUERY_KEY } from '../../../../utilities/sharedQueries';
import { TRANSACTION_CALCULATE_FEES, TRANSACTION_SEND } from '../../mutations';
import { errorCodeIsRiskAssessmentWarning } from '../../../../utilities/helpers';
import { showAlert } from '../../../../utilities/notifications';
import { currencies } from '../../../../utilities/constants';
import { useSetting, useCurrency } from '../../../../utilities/sharedHooks/settings';
import Alert from '../../../../components/Alert';

const ConfirmTransaction = ({ openScreen, onCloseModal, options, onSent }) => {
  const {
    coin,
    recipient,
    source,
    fees,
    amount,
    isLedgerAddress,
    ledgerAccountId,
    isWhitelistAddress,
    walletBalance,
    memo,
  } = options;
  const { t: translate } = useTranslation();
  const { data: userData } = useQuery(USER_DETAILS_QUERY_KEY);
  const { client } = useWalletAPIClient();
  if (isLedgerAddress && ledgerAccountId && client && !isWhitelistAddress) {
    client.account.receive(ledgerAccountId);
  }

  const [newFees, setNewFees] = useState({});

  const [totpFeatureEnabled] = useSetting('app_features', 'feature_allow_totp');

  const [enableTransactionOTP] = useSetting('app_features', 'feature_enable_transactions_otp');
  const [enableTransactionTOTP] = useSetting('app_features', 'feature_enable_transactions_totp');

  const receptionAddress = recipient;

  const currency = currencies[coin];
  const walletCurrency = useCurrency(coin.toUpperCase());

  const isSendingAmountLessThanFee = new BigNumber(fees.sendAmount).lte(fees.fee);
  const isSubDustTokenError =
    coin !== 'eth' && !walletCurrency?.is_erc20_token && isSendingAmountLessThanFee;

  const isSendingAmountLessThanZero = new BigNumber(fees.sendAmount).lte(0);
  const isAmountLessThanZero = isSendingAmountLessThanZero || isSubDustTokenError;

  const totalAmountIfLessThanZero = parseFloat(fees.fee) + parseFloat(amount);

  const { isLoading: isLoadingFees, mutate: calculateTransactionFees } = useMutation(
    TRANSACTION_CALCULATE_FEES,
    {
      onSettled(data, error) {
        if (!error) {
          if (parseFloat(data?.totalAmount) > parseFloat(walletBalance)) {
            showAlert({ message: translate('wallet_balance_less_than_new_fees'), type: 'danger' });
            openScreen(SendCryptoScreensFlow.SEND_CRYPTO);
          } else {
            setNewFees(data);
          }
        }
      },
    }
  );

  const { isLoading: isSendingTransaction, mutate: sendTransaction } = useMutation(
    TRANSACTION_SEND,
    {
      onSettled(_, error) {
        if (!error) {
          showAlert({
            message: translate('send_transaction_success_message')
              .replace('__AMOUNT__', amount)
              .replace('__COIN__', coin?.toUpperCase()),
            type: 'success',
          });
          onSent();
        } else if (errorCodeIsRiskAssessmentWarning(error.errorCode)) {
          showAlert({
            message: error.message,
            type: 'warning',
          });
        } else {
          showAlert({ message: error.message, type: 'danger' });
        }
        onCloseModal();
      },
    }
  );

  useEffect(() => {
    calculateTransactionFees({
      source: options.source,
      destination: options.recipient,
      amount: options.amount,
      mode: SEND_MODES.ADDRESS,
      coin: options.coin,
      currencyId: options?.coin_id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    if (
      (enableTransactionOTP || enableTransactionTOTP) &&
      ((enableTransactionOTP && userData.user.fa2_activated) ||
        (enableTransactionTOTP &&
          totpFeatureEnabled &&
          userData?.user?.totp_active &&
          userData?.user?.totp_enabled))
    ) {
      openScreen(SendCryptoScreensFlow.VERIFY_TRANSACTION, {
        ...options,
        fees: newFees,
        higherFeesApplied: parseFloat(fees.fee) < parseFloat(newFees?.fee),
      });
    } else if (parseFloat(fees.fee) < parseFloat(newFees?.fee)) {
      openScreen(SendCryptoScreensFlow.CONFIRM_NEW_AMOUNT_TRANSACTION, {
        ...options,
        fees: newFees,
      });
    } else {
      sendTransaction({
        amount: fees?.sendAmount,
        coin,
        currencyId: options?.coin_id,
        source,
        recipient,
        fee: fees.fee,
        memo,
      });
    }
  };

  const totalAmount = useMemo(() => {
    if (isAmountLessThanZero) {
      return formatNumber(totalAmountIfLessThanZero, currency.decimal);
    }
    return formatNumber(fees.totalAmount, currency.decimal);
  }, [currency.decimal, fees.totalAmount, isAmountLessThanZero, totalAmountIfLessThanZero]);

  const renderFooter = () => (
    <button
      className="btn btn-primary py-3"
      type="button"
      loading={isSendingTransaction || isLoadingFees ? 'true' : 'false'}
      onClick={onSubmit}>
      {translate('confirm_button_label')}
    </button>
  );

  return (
    <ModalBodyLayout
      withBack
      renderFooter={!isAmountLessThanZero ? renderFooter : undefined}
      title={
        isAmountLessThanZero
          ? translate('insufficient_transaction_label')
          : translate('confirm_transaction_label')
      }
      onPrevClick={() => openScreen(SendCryptoScreensFlow.SEND_CRYPTO)}>
      {!isAmountLessThanZero && (
        <>
          <p className="text-shadow text-f6 mt-3">{translate('reception_label')}</p>
          <p
            className="text-f5"
            style={{
              wordBreak: 'break-word',
              width: !isMobile && '25rem',
            }}>
            {receptionAddress}
          </p>
        </>
      )}

      <div className="mt-4">
        <div className="bg-light rounded-2">
          {isAmountLessThanZero && (
            <div className="d-flex align-content-between justify-content-between p-3">
              <b className="text-f5 text-shadow">{translate('available_balance_label')}</b>
              <p className="text-dark text-f5 upper-case">
                <b>
                  {formatNumber(walletBalance, currency.decimal)} {coin}
                </b>
              </p>
            </div>
          )}

          <div className="d-flex align-content-between justify-content-between p-3">
            <b className="text-f5 text-shadow">{translate('amount_label')}</b>
            <p className="text-dark text-f5 upper-case">
              <b>
                {isAmountLessThanZero
                  ? formatNumber(amount, currency.decimal)
                  : formatNumber(fees.sendAmount, currency.decimal)}{' '}
                {coin}
              </b>
            </p>
          </div>

          <div className="d-flex align-content-between justify-content-between p-3 ">
            <b className="text-f5 text-shadow">{translate('fee_label')}</b>
            <p className="text-dark text-f5 upper-case">
              <b>
                {formatNumber(fees.fee, currency.decimal)} {coin}
              </b>
            </p>
          </div>
        </div>

        <div className="d-flex align-content-between justify-content-between p-3 mt-3 bg-light rounded-2">
          <b className="text-f5 text-shadow">{translate('total_cost_label')}</b>
          <p className="text-dark text-f5 upper-case">
            <b>
              {totalAmount} {coin}
            </b>
          </p>
        </div>
        {isAmountLessThanZero ? (
          <>
            <div className="mt-4">
              {isSubDustTokenError ? (
                <Alert type="danger" icon="cancel">
                  {translate('fee_alert_error_sub_dust')}
                </Alert>
              ) : (
                <Alert type="danger" icon="cancel">
                  {translate('fee_alert_error')}
                </Alert>
              )}
            </div>
            <div className="mt-4">
              <Alert type="info" icon="info">
                {translate('fee_alert_warning')}
              </Alert>
            </div>
          </>
        ) : (
          <div className="mt-4">
            <Alert type="info" icon="info">
              {translate('fee_alert_info')}
            </Alert>
          </div>
        )}
      </div>
    </ModalBodyLayout>
  );
};

export default ConfirmTransaction;

ConfirmTransaction.propTypes = {
  openScreen: PropTypes.func.isRequired,
  options: PropTypes.object,
  onCloseModal: PropTypes.func.isRequired,
  onSent: PropTypes.func,
};
ConfirmTransaction.defaultProps = {
  options: null,
  onSent: () => {},
};
